/* global Word */

import { INSPECTION_STATUS } from '../FinanceOfficeOperationRegistry';
import {
  assertCurrentDocumentIdentity,
  assertNotAborted,
  containsLicensedDataEvidence,
  targetDrift,
  verificationFailed,
  writerError,
} from './common';

function expectedText(request) {
  return [
    ...request.payload.paragraphs,
    '',
    ...request.payload.citationLines,
  ].join('\r');
}

function normalizedWordText(value) {
  return String(value || '').replace(/\r+$/, '');
}

export class WordFinanceWriter {
  constructor({
    run = (callback) => {
      if (typeof Word === 'undefined' || typeof Word.run !== 'function') {
        throw writerError(
          'finance_office_unavailable',
          'Word API is unavailable'
        );
      }
      return Word.run(callback);
    },
  } = {}) {
    this.run = run;
    this.supportsReadOnlyLiveDataTarget = false;
  }

  async detectLicensedDataEvidence() {
    return this.run(async (context) => {
      const body = context.document.body;
      body.load('text');
      await context.sync();
      return containsLicensedDataEvidence(body.text);
    });
  }

  async prepareLiveDataTarget({
    signal,
  } = {}) {
    assertNotAborted(signal);
    throw writerError(
      'finance_office_target_unavailable',
      'Word live data is unavailable until a stable read-only target is supported'
    );
  }

  async _loadControls(context, request) {
    const controls = context.document.contentControls.getByTag(
      request.target.contentControlTag
    );
    controls.load('items/id,items/tag');
    await context.sync();
    if (controls.items.length > 1) {
      throw targetDrift('The Word operation content control is ambiguous');
    }
    if (controls.items.length === 0) return { controls, control: null, text: '' };
    const control = controls.items[0];
    const range = control.getRange();
    range.load('text');
    await context.sync();
    return { controls, control, range, text: normalizedWordText(range.text) };
  }

  async prepare(request, {
    signal,
    assertCurrentIdentity,
  } = {}) {
    assertNotAborted(signal);
    return this.run(async (context) => {
      const target = await this._loadControls(context, request);
      assertNotAborted(signal);
      await assertCurrentDocumentIdentity(assertCurrentIdentity);
      if (!target.control) {
        throw targetDrift('The Word operation content control no longer exists');
      }
      if (target.text !== '') {
        throw targetDrift('The Word operation content control is not empty');
      }
      return {
        targetType: 'content_control',
        stableTargetId: request.target.contentControlTag,
      };
    });
  }

  async inspect(request, {
    signal,
    assertCurrentIdentity,
  } = {}) {
    assertNotAborted(signal);
    return this.run(async (context) => {
      const target = await this._loadControls(context, request);
      await assertCurrentDocumentIdentity(assertCurrentIdentity);
      if (!target.control || target.text === '') {
        return { status: INSPECTION_STATUS.NOT_COMMITTED, mutationCount: 0 };
      }
      return {
        status: target.text === expectedText(request)
          ? INSPECTION_STATUS.COMMITTED
          : INSPECTION_STATUS.INDETERMINATE,
        mutationCount: request.payload.paragraphs.length +
          request.payload.citationLines.length,
      };
    });
  }

  async commit(request, {
    signal,
    assertCurrentIdentity,
  } = {}) {
    assertNotAborted(signal);
    return this.run(async (context) => {
      let target = await this._loadControls(context, request);
      assertNotAborted(signal);
      await assertCurrentDocumentIdentity(assertCurrentIdentity);
      const expected = expectedText(request);
      if (target.control && target.text !== '') {
        throw targetDrift('The Word operation content control changed before commit');
      }
      if (!target.control) {
        throw targetDrift('The Word operation content control no longer exists');
      }
      assertNotAborted(signal);
      await assertCurrentDocumentIdentity(assertCurrentIdentity);
      target.control.insertText(expected, Word.InsertLocation.replace);
      await context.sync();
      const verificationRange = target.control.getRange();
      verificationRange.load('text');
      await context.sync();
      if (normalizedWordText(verificationRange.text) !== expected) {
        throw verificationFailed();
      }
      return {
        committed: true,
        mutationCount: request.payload.paragraphs.length +
          request.payload.citationLines.length,
      };
    });
  }
}

export default WordFinanceWriter;
